const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
//
// Revealing is one-way and idempotent (2026-09-11 QA round 5, item 3).
// Dawn's original only revealed an element on an isIntersecting callback,
// and re-hid it on every non-intersecting one. Two things went wrong with
// that on a long grid:
//
//   - A fast scroll or a programmatic jump can carry an element from below
//     the viewport to above it between two observation frames, so the
//     observer only ever computes false -> false and the element keeps
//     scroll-trigger--offscreen for good. That is the "some cards were
//     still faded once the viewport stopped" case.
//   - The first callback after observe() is asynchronous and low priority,
//     so elements already on screen at attach stayed at opacity 0.01 for a
//     visible beat — longer while the main thread is busy during a fling.
//
// So: reveal() is the only path that touches the class, it unobserves as
// it goes, an element that has scrolled past the top is revealed rather
// than re-hidden, and observe() is followed by one synchronous pass so
// anything already in view is never waiting on the observer at all.
function reveal(element, index) {
  if (element.hasAttribute('data-cascade')) element.setAttribute('style', `--animation-order: ${index};`);
  // --offscreen is the only thing holding the element at opacity 0.01
  // (base.css); removing a class that was never added is a no-op, which is
  // what makes this safe to call on an element that is already visible.
  element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
}

// Anything whose top has crossed the bottom of the viewport has had its
// cue, whether or not the observer ever saw it intersect — including
// everything already scrolled past above. The 50px matches the observer's
// own rootMargin so both paths reveal at the same line.
function hasBeenSeen(element) {
  return element.getBoundingClientRect().top < window.innerHeight - 50;
}

// Reveals a group together, so data-cascade staggers within the group the
// way Dawn's batch index did rather than by position in the document.
function revealGroup(elements, observer, pending) {
  elements.forEach((element, index) => {
    reveal(element, index);
    observer.unobserve(element);
    pending.delete(element);
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const pending = new Set(animationTriggerElements);

  const observer = new IntersectionObserver((entries) => {
    const ready = [];
    entries.forEach((entry) => {
      if (entry.isIntersecting || hasBeenSeen(entry.target)) {
        ready.push(entry.target);
      } else {
        entry.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        entry.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
      }
    });
    revealGroup(ready, observer, pending);
    stopWhenDone();
  }, { rootMargin: '0px 0px -50px 0px' });

  // The sweep. An element that goes from below the viewport to above it
  // between two observation frames — a fling, or any programmatic jump —
  // is never intersecting at either sample, so the observer has no change
  // to report and never calls back for it at all. Without this it keeps
  // scroll-trigger--offscreen for the life of the page. Measured on
  // /collections/all: a jump to the middle left eight elements hidden
  // permanently, one of them a card in view.
  const sweep = () => {
    const ready = [];
    pending.forEach((element) => {
      if (hasBeenSeen(element)) ready.push(element);
    });
    revealGroup(ready, observer, pending);
    stopWhenDone();
  };

  const onScroll = throttle(sweep, 100);

  // Nothing is left to reveal, so the page stops paying for the listener.
  function stopWhenDone() {
    if (pending.size > 0) return;
    window.removeEventListener('scroll', onScroll);
    observer.disconnect();
  }

  animationTriggerElements.forEach((element) => observer.observe(element));
  window.addEventListener('scroll', onScroll, { passive: true });

  // The synchronous half of the rule: whatever is already on screen at
  // attach reveals now rather than one observer tick later.
  sweep();
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
